import i18n from '@auw/i18n';
import uploadLimits from '@auw/platform-contracts/upload-limits.json';

export interface AttachmentConstraintItem {
  name?: string;
  size?: number | null;
}

export const CHAT_ATTACHMENT_MAX_COUNT = uploadLimits.fileCount;
export const CHAT_ATTACHMENT_MAX_BYTES = uploadLimits.fileBytes;

export const MODAL_STALE_TIME = 24 * 60 * 60 * 1000; // 24h
export const MODAL_GC_TIME = 24 * 60 * 60 * 1000;    // 24h

export const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  // 이미지
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg',
  // 문서
  '.pdf', '.doc', '.docx', '.hwp', '.hwpx', '.xls', '.xlsx', '.xlsm', '.csv', '.txt', '.md',
  '.pptx', '.ppt',
  // 오디오
  '.aac', '.m4a', '.mp3', '.wav', '.ogg',
  // 비디오
  '.mp4', '.webm',
  // 데이터/코드
  '.json', '.xml', '.yaml', '.yml', '.py', '.js', '.ts', '.html', '.css',
]);

export function formatAttachmentLimitSize(bytes: number = CHAT_ATTACHMENT_MAX_BYTES): string {
  const megaBytes = bytes / (1024 * 1024);
  if (Number.isInteger(megaBytes)) return `${megaBytes}MB`;
  return `${megaBytes.toFixed(1)}MB`;
}

export function isAttachmentSizeAllowed(size?: number | null): boolean {
  return typeof size !== 'number' || size <= CHAT_ATTACHMENT_MAX_BYTES;
}

export function getRemainingAttachmentSlots(existingCount: number, selectedCount: number = 0): number {
  return Math.max(0, CHAT_ATTACHMENT_MAX_COUNT - Math.max(0, existingCount) - Math.max(0, selectedCount));
}

function formatAttachmentListLabel(items: readonly AttachmentConstraintItem[]): string {
  const unnamed = i18n.t('common:chat.unnamedFile');
  const names = items
    .map((item) => (item.name || unnamed).trim() || unnamed)
    .slice(0, 2);

  if (items.length === 1) return `"${names[0]}"`;
  if (items.length === 2) return `"${names[0]}", "${names[1]}"`;
  return `"${names[0]}", "${names[1]}" ${i18n.t('common:chat.andMore', { count: items.length - 2 })}`;
}

export function buildAttachmentSizeLimitToast(items: readonly AttachmentConstraintItem[]): {
  title: string;
  description: string;
} {
  const limitLabel = formatAttachmentLimitSize();
  const files = formatAttachmentListLabel(items);
  if (items.length === 1) {
    return {
      title: i18n.t('common:chat.fileSizeExceeded'),
      description: i18n.t('common:chat.fileSizeLimitSingle', { files, limit: limitLabel }),
    };
  }

  return {
    title: i18n.t('common:chat.fileSizeExceeded'),
    description: i18n.t('common:chat.fileSizeLimitMultiple', { files, limit: limitLabel }),
  };
}

export function isAttachmentTypeAllowed(name?: string): boolean {
  if (!name) return true;
  const dotIdx = name.lastIndexOf('.');
  if (dotIdx === -1) return true;
  const ext = name.slice(dotIdx).toLowerCase();
  return ALLOWED_ATTACHMENT_EXTENSIONS.has(ext);
}

export function buildAttachmentTypeLimitToast(items: readonly AttachmentConstraintItem[]): {
  title: string;
  description: string;
} {
  const extList = items
    .map((item) => {
      const name = item.name || '';
      const dotIdx = name.lastIndexOf('.');
      return dotIdx !== -1 ? name.slice(dotIdx).toLowerCase() : '';
    })
    .filter(Boolean);
  const uniqueExts = [...new Set(extList)];
  const extLabel = uniqueExts.length > 0 ? uniqueExts.join(', ') : '';
  const files = formatAttachmentListLabel(items);

  if (items.length === 1) {
    return {
      title: i18n.t('common:chat.unsupportedFormat'),
      description: i18n.t('common:chat.unsupportedFormatSingle', { files, ext: extLabel }),
    };
  }

  return {
    title: i18n.t('common:chat.unsupportedFormat'),
    description: i18n.t('common:chat.unsupportedFormatMultiple', { files, count: items.length, ext: extLabel }),
  };
}

export function buildAttachmentCountLimitToast(skippedCount?: number): {
  title: string;
  description: string;
} {
  if (typeof skippedCount === 'number' && skippedCount > 0) {
    return {
      title: i18n.t('common:chat.fileCountExceeded'),
      description: i18n.t('common:chat.fileCountLimitWithSkipped', { max: CHAT_ATTACHMENT_MAX_COUNT, skipped: skippedCount }),
    };
  }

  return {
    title: i18n.t('common:chat.fileCountExceeded'),
    description: i18n.t('common:chat.fileCountLimitDesc', { max: CHAT_ATTACHMENT_MAX_COUNT }),
  };
}

export interface AttachmentBatchValidationResult<T extends AttachmentConstraintItem> {
  accepted: T[];
  rejectedBySize: T[];
  rejectedByCount: T[];
  rejectedByType: T[];
}

export function validateAttachmentBatch<T extends AttachmentConstraintItem>(
  incoming: readonly T[],
  existingCount: number,
): AttachmentBatchValidationResult<T> {
  const accepted: T[] = [];
  const rejectedBySize: T[] = [];
  const rejectedByCount: T[] = [];
  const rejectedByType: T[] = [];
  const remainingSlots = getRemainingAttachmentSlots(existingCount);

  for (const item of incoming) {
    if (!isAttachmentTypeAllowed(item.name)) {
      rejectedByType.push(item);
      continue;
    }

    if (!isAttachmentSizeAllowed(item.size)) {
      rejectedBySize.push(item);
      continue;
    }

    if (accepted.length >= remainingSlots) {
      rejectedByCount.push(item);
      continue;
    }

    accepted.push(item);
  }

  return { accepted, rejectedBySize, rejectedByCount, rejectedByType };
}
